import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronRight, ArrowUpRight, Loader2, ShoppingBag, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";
import { blogPostingLd, breadcrumbLd, faqLd } from "@/components/seo/jsonld";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_LABELS = {
  product: "Product deep-dive",
  comparison: "Comparison",
  buying_guide: "Buying guide",
  authenticity: "Authenticity",
  regulatory: "Regulatory",
  news: "Market news",
  clinical: "Clinical",
  price_guide: "Price guide",
};

// Volume tiers (mirror PDP and backend)
const TIERS = [
  { range: "1-10", pct: 0 },
  { range: "11-20", pct: 4 },
  { range: "21-30", pct: 5 },
  { range: "31-50", pct: 6 },
  { range: "51+", pct: 8 },
];

export const JournalPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setPost(null);
    setError(false);
    axios.get(`${API}/journal/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        // Fetch related — same product OR same topic
        return axios.get(`${API}/journal/posts?category=${r.data.product_category}&limit=4`);
      })
      .then((r) => {
        if (r) setRelated((r.data.posts || []).filter((p) => p.slug !== slug).slice(0, 3));
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <>
        <Seo title="Article not found" path={`/journal/${slug}`} noindex />
        <Header />
        <div className="max-w-[1400px] mx-auto px-6 pt-32 pb-32 text-center">
          <h1 className="font-heading text-4xl font-black">Article not found</h1>
          <Link to="/journal" className="mt-6 inline-block border-b border-black pb-1 text-sm font-medium">← Back to journal</Link>
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Header />
        <div className="max-w-[1400px] mx-auto px-6 pt-32 pb-32 flex items-center justify-center text-neutral-500">
          <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading article…
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={post.title}
        description={post.meta_description || post.subtitle}
        keywords={(post.keywords || post.secondary_keywords || []).join(", ")}
        path={`/journal/${post.slug}`}
        ogType="article"
        ogImage={post.image}
        ogImageAlt={post.image_alt}
        jsonLd={[
          blogPostingLd({
            id: post.slug,
            title: post.title,
            excerpt: post.subtitle,
            image: post.image,
            author: post.author,
            date: post.published_at,
            category: TOPIC_LABELS[post.topic_type] || post.topic_type,
          }),
          breadcrumbLd([
            { name: "Home", url: "/" },
            { name: "Journal", url: "/journal" },
            { name: post.title, url: `/journal/${post.slug}` },
          ]),
          post.faqs?.length ? faqLd(post.faqs) : null,
        ].filter(Boolean)}
      />
      <Header />

      <article className="max-w-[1200px] mx-auto px-6 pt-28 md:pt-32 pb-12" data-testid="journal-post-page">
        <nav className="flex items-center gap-2 text-xs text-neutral-500 mb-10" data-testid="breadcrumb">
          <Link to="/" className="hover:text-black uppercase tracking-[0.2em]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/journal" className="hover:text-black uppercase tracking-[0.2em]">Journal</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-black uppercase tracking-[0.2em] truncate">{TOPIC_LABELS[post.topic_type]}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Article column */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-neutral-500 font-medium mb-4">
              <span>{TOPIC_LABELS[post.topic_type] || post.topic_type}</span>
              <span className="w-1 h-1 bg-neutral-400 rounded-full" />
              <span>{post.reading_time_min} min read</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-5" data-testid="post-title">
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="text-lg text-neutral-600 font-light leading-relaxed mb-7">{post.subtitle}</p>
            )}
            <div className="flex items-center gap-4 pb-7 mb-10 border-b border-neutral-200 text-sm">
              <span className="font-bold">{post.author}</span>
              <span className="text-neutral-400">·</span>
              <time className="text-neutral-500">{new Date(post.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time>
            </div>

            <div className="aspect-[16/9] overflow-hidden bg-neutral-100 mb-10">
              <img
                src={post.image}
                alt={post.image_alt || post.title}
                width="1600"
                height="900"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Intro paragraph */}
            {post.intro && (
              <p className="text-lg md:text-xl text-neutral-800 font-light leading-relaxed mb-10 first-letter:text-5xl first-letter:font-heading first-letter:font-black first-letter:float-left first-letter:mr-3 first-letter:mt-1" data-testid="post-intro">
                {post.intro}
              </p>
            )}

            {/* Sections */}
            <div className="space-y-10">
              {(post.sections || []).map((s, i) => (
                <section key={i} data-testid={`section-${i}`}>
                  <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight leading-tight mb-4">{s.h}</h2>
                  <div className="prose-content text-base md:text-lg text-neutral-800 font-light leading-relaxed">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                      p: ({ node, ...props }) => <p className="mb-4" {...props} />,
                      strong: ({ node, ...props }) => <strong className="font-bold text-black" {...props} />,
                      ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4 space-y-1.5" {...props} />,
                      ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-4 space-y-1.5" {...props} />,
                      table: ({ node, ...props }) => <div className="my-6 overflow-x-auto"><table className="w-full text-sm border border-neutral-200" {...props} /></div>,
                      thead: ({ node, ...props }) => <thead className="bg-neutral-50 text-left" {...props} />,
                      th: ({ node, ...props }) => <th className="px-3 py-2 font-bold border-b border-neutral-200" {...props} />,
                      td: ({ node, ...props }) => <td className="px-3 py-2 border-b border-neutral-100" {...props} />,
                      a: ({ node, ...props }) => <a className="border-b border-black pb-0.5 hover:opacity-70" {...props} />,
                    }}>
                      {s.body || ""}
                    </ReactMarkdown>
                  </div>
                </section>
              ))}
            </div>

            {/* Trust block — authenticity & traceability callout */}
            {post.trust_block?.body && (
              <div className="mt-12 p-6 border-l-4 border-black bg-neutral-50" data-testid="trust-block">
                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 font-medium">
                  {post.trust_block.title || "Authenticity & traceability"}
                </div>
                <p className="text-base text-neutral-800 font-light leading-relaxed">
                  {post.trust_block.body}
                </p>
              </div>
            )}

            {/* FAQ */}
            {post.faqs?.length > 0 && (
              <section className="mt-14" data-testid="post-faqs">
                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Frequently asked</div>
                <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight mb-6">For professional buyers.</h2>
                <div className="space-y-3">
                  {post.faqs.map((f, i) => (
                    <details key={i} className="group bg-white border border-neutral-200" data-testid={`faq-${i}`}>
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-5 font-heading font-bold">
                        <span>{f.q}</span>
                        <span className="text-2xl font-thin group-open:rotate-45 transition-transform">+</span>
                      </summary>
                      <div className="px-5 pb-5 text-sm text-neutral-700 font-light leading-relaxed">{f.a}</div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* CTA — final */}
            <div className="mt-14 p-8 bg-black text-white" data-testid="cta-final">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-400 mb-3 font-medium">Wholesale offer</div>
              <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight mb-3">
                {post.cta_copy || `Order ${post.product_name} wholesale today.`}
              </h3>
              <p className="text-sm md:text-base text-neutral-300 font-light mb-5 max-w-xl">
                Authentic, lot-traceable and cold-chain shipped to verified clinics. Volume pricing unlocks automatically — save up to 8% on orders of 51+ units.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to={`/product/${post.product_slug || post.product_id}`}
                  className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 font-medium text-sm hover:bg-neutral-100"
                  data-testid="cta-buy-btn"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Buy {post.product_name} wholesale
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <span className="text-sm text-neutral-400">
                  From <span className="text-white font-bold">${Number(post.product_price || 0).toFixed(2)}</span> / unit
                </span>
              </div>
            </div>

            {/* Back link */}
            <div className="mt-12 pt-8 border-t border-neutral-200 text-sm">
              <Link to="/journal" className="font-medium border-b border-black pb-0.5">← Back to journal</Link>
            </div>
          </div>

          {/* Sticky volume widget */}
          <aside className="lg:col-span-4">
            <div className="sticky top-32 space-y-6" data-testid="volume-widget">
              <div className="border border-neutral-200 p-6 bg-white">
                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Volume pricing</div>
                <h4 className="font-heading text-xl font-black tracking-tight mb-1 truncate">{post.product_name}</h4>
                <div className="font-heading text-3xl font-black tracking-tight mb-5">
                  ${Number(post.product_price || 0).toFixed(2)}
                  <span className="text-sm font-medium text-neutral-500 ml-2">/ unit</span>
                </div>
                <table className="w-full text-sm" data-testid="tier-table">
                  <thead className="border-b border-neutral-200">
                    <tr className="text-left">
                      <th className="py-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">Qty</th>
                      <th className="py-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">Discount</th>
                      <th className="py-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium text-right">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TIERS.map((t) => {
                      const unit = Number(post.product_price || 0) * (1 - t.pct / 100);
                      return (
                        <tr key={t.range} className="border-b border-neutral-100 last:border-0">
                          <td className="py-2.5 font-medium">{t.range}</td>
                          <td className="py-2.5">
                            {t.pct > 0 ? <span className="text-emerald-700 font-bold">-{t.pct}%</span> : <span className="text-neutral-400">—</span>}
                          </td>
                          <td className="py-2.5 text-right font-bold">${unit.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Link
                  to={`/product/${post.product_slug || post.product_id}`}
                  className="mt-5 block text-center bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800"
                  data-testid="widget-cta-btn"
                >
                  View product →
                </Link>
                <ul className="mt-4 space-y-1.5 text-xs text-neutral-500 font-light">
                  <li>· Cold-chain shipping included</li>
                  <li>· Free shipping over $699</li>
                  <li>· Lot certificates on request</li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-neutral-50 border-t border-neutral-200" data-testid="related-posts">
          <div className="max-w-[1400px] mx-auto px-6 py-16">
            <h3 className="font-heading text-2xl font-black tracking-tight mb-8">More from this category.</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-px bg-neutral-200 border border-neutral-200">
              {related.map((p) => (
                <Link key={p.id} to={`/journal/${p.slug}`} className="bg-white p-6 flex flex-col gap-3 group" data-testid={`related-${p.id}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{TOPIC_LABELS[p.topic_type]}</div>
                  <h4 className="font-heading text-lg font-black tracking-tight group-hover:underline underline-offset-4 line-clamp-3">{p.title}</h4>
                  <div className="mt-auto inline-flex items-center gap-2 text-xs font-medium">Read <ArrowUpRight className="w-3.5 h-3.5" /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
};
